import { useEffect, useRef, useState } from "react";
import { Tooltip } from "@mui/material";
import VisibilityOutlinedIcon from "@mui/icons-material/VisibilityOutlined";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import { toast } from "react-toastify";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../../../components/Table";
import TableLayout from "../../../components/layout/TableLayout";
import Button from "../../../components/ui/Button";
import useModal from "../../../hooks/useModal";
import useConfirm from "../../../hooks/useConfirm";
import useLoader from "../../../hooks/useLoader";
import AddClearanceDepartmentForm from "../../../components/exit-management/AddClearanceDepartmentForm";
import ViewClearanceDepartment from "../../../components/exit-management/ViewClearanceDepartment";
import {
  createNocClearanceDepartment,
  deleteNocClearanceDepartment,
  fetchDepartments,
  getNocClearanceDepartmentDetails,
  getNocClearanceDepartments,
  updateNocClearanceDepartment,
} from "../../../services/exitManagement";

const SettingTab = () => {
  const { showModal } = useModal();
  const { confirm } = useConfirm();
  const { showLoader, hideLoader } = useLoader();
  const [clearanceDepartments, setClearanceDepartments] = useState([]);
  // Department dropdown options, loaded the first time Add/Edit is opened
  const departmentOptionsRef = useRef(null);

  const fetchClearanceDepartments = async () => {
    showLoader();
    try {
      const { status, data } = await getNocClearanceDepartments();
      setClearanceDepartments(status === 200 ? data.result || [] : []);
    } catch (error) {
      console.error("Error fetching clearance departments:", error);
      setClearanceDepartments([]);
    } finally {
      hideLoader();
    }
  };

  useEffect(() => {
    fetchClearanceDepartments();
    // eslint-disable-next-line
  }, []);

  const getDepartmentOptions = async () => {
    if (departmentOptionsRef.current) return departmentOptionsRef.current;
    const response = await fetchDepartments();
    departmentOptionsRef.current = (response?.data?.data || []).map(
      (dept) => ({
        id: String(dept._DeptID),
        name: dept._DeptName,
      }),
    );
    return departmentOptionsRef.current;
  };

  // Departments already configured are not offered again, except the one being edited
  const getAvailableDepartments = async (currentDepartmentId = null) => {
    const options = await getDepartmentOptions();
    const addedDepartmentIds = clearanceDepartments
      .map((d) => String(d.departmentId))
      .filter((id) => id !== String(currentDepartmentId));
    return options.filter((dept) => !addedDepartmentIds.includes(dept.id));
  };

  const handleAddDepartmentModal = async () => {
    showLoader();
    try {
      const departments = await getAvailableDepartments();
      showModal(
        "Add Clearance Department",
        <AddClearanceDepartmentForm
          departments={departments}
          onSave={async (payload) => {
            const response = await createNocClearanceDepartment(payload);
            fetchClearanceDepartments();
            return response;
          }}
        />,
        "md",
      );
    } catch (error) {
      console.error("Error loading departments:", error);
      toast.error("Failed to load departments");
    } finally {
      hideLoader();
    }
  };

  // Questions are fetched only when a department is opened
  const loadDepartmentDetails = async (id) => {
    const { data } = await getNocClearanceDepartmentDetails(id);
    return data.result;
  };

  const handleEditDepartmentModal = async (item) => {
    showLoader();
    try {
      const [departments, details] = await Promise.all([
        getAvailableDepartments(item.departmentId),
        loadDepartmentDetails(item._id),
      ]);
      showModal(
        "Edit Clearance Department",
        <AddClearanceDepartmentForm
          departments={departments}
          initialData={details}
          onSave={async (payload) => {
            const response = await updateNocClearanceDepartment(
              item._id,
              payload,
            );
            fetchClearanceDepartments();
            return response;
          }}
        />,
        "md",
      );
    } catch (error) {
      console.error("Error loading clearance department:", error);
      toast.error(
        error?.response?.data?.message || "Failed to load department",
      );
    } finally {
      hideLoader();
    }
  };

  const handleViewDepartmentModal = async (item) => {
    showLoader();
    try {
      const details = await loadDepartmentDetails(item._id);
      showModal(
        "Clearance Department Details",
        <ViewClearanceDepartment department={details} />,
        "md",
      );
    } catch (error) {
      console.error("Error loading clearance department:", error);
      toast.error(
        error?.response?.data?.message || "Failed to load department",
      );
    } finally {
      hideLoader();
    }
  };

  const handleDelete = async (id) => {
    try {
      const isConfirmed = await confirm({
        title: "Confirm Delete",
        content: "Are you sure you want to delete this department?",
        confirmText: "Yes, Delete",
        cancelText: "Cancel",
      });
      if (!isConfirmed) return;

      await deleteNocClearanceDepartment(id);
      toast.success("Department deleted successfully!");
      fetchClearanceDepartments();
    } catch (error) {
      console.log("Error in delete department");
      toast.error(
        error?.response?.data?.message || "Error in delete department",
      );
    }
  };

  return (
    <div className="content-block">
      <div className="d-flex justify-content-between align-items-center mb-3">
        <h5 className="mb-0">Departments & Checklist</h5>
        <Button
          variant="success"
          size="small"
          className="d-inline-flex align-items-center gap-1"
          onClick={handleAddDepartmentModal}
        >
          <span className="material-icons-outlined" style={{ fontSize: "18px" }}>
            add_box
          </span>
          <span className="text">Add Department</span>
        </Button>
      </div>

      <TableLayout>
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>S.No.</TableHead>
              <TableHead>Department</TableHead>
              <TableHead>Total Questions</TableHead>
              <TableHead>Action</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {clearanceDepartments.length > 0 ? (
              clearanceDepartments.map((item, index) => (
                <TableRow key={item._id}>
                  <TableCell>{index + 1}</TableCell>
                  <TableCell>{item.departmentName}</TableCell>
                  <TableCell>{item.questionsCount ?? 0}</TableCell>
                  <TableCell>
                    <Tooltip title="View Details" placement="top" arrow>
                      <VisibilityOutlinedIcon
                        className="cursor-pointer"
                        onClick={() => handleViewDepartmentModal(item)}
                      />
                    </Tooltip>
                    <Tooltip title="Edit Department" placement="top" arrow>
                      <EditIcon
                        className="cursor-pointer"
                        onClick={() => handleEditDepartmentModal(item)}
                      />
                    </Tooltip>
                    <Tooltip title="Delete Department" placement="top" arrow>
                      <DeleteIcon
                        className="cursor-pointer"
                        onClick={() => handleDelete(item._id)}
                      />
                    </Tooltip>
                  </TableCell>
                </TableRow>
              ))
            ) : (
              <TableRow>
                <TableCell colSpan="4" className="text-center">
                  No records found
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableLayout>
    </div>
  );
};

export default SettingTab;
